import React, {useContext, useEffect, useRef, useState} from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row
} from 'reactstrap';
import {Breadcrumbs, Btn, H6, Image} from '../../../../AbstractElements';
import {useParams} from "react-router";
import axiosConfig from "../../../../Config/axios";
import Loader from "../../../../Layout/Loader";
import {BootstrapTabContainer, BootstrapTabContent} from "../../../../CommonElements/BootstrapTabs";
import RoleListView from "../../Role/Component/RoleListView";
import PermissionListView from "../../Permission/Component/PermissionListView";
import ButtonIcon from "../../../../CommonElements/ButtonIcon";
import UpdateUserForm from "../Component/UpdateUserForm";
import UpdatePasswordForm from "../Component/UpdatePasswordForm";
import {ActivityLog} from "../../../SystemCore/ActivityLog";
import WalletTransactionsTable from "../../../Application/Wallet/WalletTransaction/Component/WalletTransactionsTable";
import {getApplicationRoute, getPrivilegeRoute} from "../../../../Route/RouteIndex";
import {useNavigate} from "react-router-dom";
import AuthorizationContext from "../../../../_helper/Authorization";
import {User} from "../Types/UserTypes";
import type {AuthorizationContextType} from "../../../../_helper/Authorization/AuthorizationTypes";
import WalletContext from "../../../Application/Wallet/Context";
import {WalletContextType} from "../../../Application/Wallet/Context/WalletContext.types";
import AdminZoneWallet from "../../../Application/Wallet/Component/AdminZoneWallet";
import ContactTable from "../../../SystemCore/Contact/Component/ContactTable";
import ContactForm from "../../../SystemCore/Contact/Component/ContactForm";
import type {Contact, ContactTableRef} from "../../../SystemCore/Contact/Types/ContactTypes";
import CollectorTable from "../../../Application/Collector/Collector/Component/CollectorTable";
import MultimediaService from "../../../SystemCore/Multimedia/Services/Multimedia.service";
import ProfileImage from "../../../CommonComponents/Profiles/ProfileImage";

const UserShow = () => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [user, setUser] = useState<User | null>(null);
    const [url, setUrl] = useState<string>('');
    const [contactModalVisibility, setContactModalVisibility] = useState<boolean>(false);
    const [selectedContact, setSelectedContact] = useState<Contact | undefined>();
    const contactTableRef = useRef<ContactTableRef>(null);
    const params = useParams();
    const navigate = useNavigate();
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const walletContext = useContext(WalletContext) as WalletContextType;
    const {errorHandler, hasPermission, authorizedUser} = authContext || {};
    const {getWalletInformation} = walletContext || {};
    const userRoute = getPrivilegeRoute('user', true);
    const canReadWalletTransactions = !!(hasPermission && hasPermission('read-wallet-transaction'));
    const canAssignUserPermission = !!(hasPermission && hasPermission('assign-user-permission'));
    const canAssignUserRole = !!(hasPermission && hasPermission('assign-user-role'));
    const canReadCollectors = !!(hasPermission && hasPermission('read-collector'));

    useEffect(() => {
        getUser();
    }, [params.user, navigate, errorHandler]);

    const getUser = () => {
        setLoaderVisibility(true);
        axiosConfig
            .get(`${getPrivilegeRoute('user')}/${params.user}`)
            .then((response: { data: { data: User; }; }) => {
                if (response?.data?.data) {
                    setUser(response.data.data);
                } else {
                    navigate(userRoute);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
                navigate(userRoute);
            });
    }

    const deleteUser = () => {
        if (!user) return;
        setLoaderVisibility(true);
        axiosConfig
            .delete(`${getPrivilegeRoute('user')}/${user.id}`)
            .then((response: { data: { data: User; }; }) => {
                setLoaderVisibility(false);
                if (response?.data?.data) {
                    navigate(userRoute);
                }
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    const contactModalToggle = () => {
        if (contactModalVisibility) {
            setSelectedContact(undefined);
        }
        setContactModalVisibility(!contactModalVisibility);
    };

    const addContact = (contact: Contact) => {
        contactTableRef.current?.addContact(contact);
        contactModalToggle();
    };

    const openContactEditModal = (contact: Contact) => {
        setSelectedContact(contact);
        setContactModalVisibility(true);
    };

    const profileImageUrl = url
        || MultimediaService.resolveMultimediaUrl(user?.avatar)
        || require('../../../../assets/images/user/7.jpg');

    return (
        <div>
            <Breadcrumbs mainTitle="User Profile" parent="Privilege" subParentLink={`${getPrivilegeRoute('user')}`}
                         subParent={'Users'} title={user?.name}/>
            <Container fluid={true}>
                <div className="user-profile">
                    <Loader visibility={loaderVisibility}/>
                    <Row>
                        <Col sm='12' lg='3'>
                            <Card className='hovercard text-center'>
                                <CardHeader className='social-profile'>
                                    <div className='d-flex align-items-center gap-2'>
                                    </div>
                                </CardHeader>
                                <ProfileImage profileImageUrl={profileImageUrl} setUrl={setUrl} params={{
                                    link_type_key: 'user',
                                    link_type_id: user?.id,
                                    single: true,
                                    role: 'avatar',
                                }}/>
                                <div className='info'>
                                    <Row className='step3' data-intro='This is the your details'>
                                        <Col sm='12' lg='12' className='order-sm-0 order-xl-1'>
                                            <div className='user-designation'>
                                                <div className='title'>
                                                    <a>
                                                        {user?.name}
                                                    </a>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm='6' lg='12' className='order-sm-1 order-xl-0'>
                                            <Row>
                                                <Col>
                                                    <div className='ttl-info text-center'>
                                                        <H6>
                                                            {user?.email}
                                                        </H6>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </div>
                                <CardBody>
                                    {user?.pointWallet?.last_transaction && user ? (
                                        getWalletInformation(user?.pointWallet)) : null}

                                    {user?.cashWallet?.last_transaction && user ? (
                                        getWalletInformation(user?.cashWallet)
                                    ) : null}

                                    {!(user?.cashWallet || user?.pointWallet) && (user?.system_roles?.includes('admin') || user?.system_roles?.includes('super-admin')) && (
                                        <AdminZoneWallet 
                                            relationKey="user" 
                                            relationId={user?.id || ''} 
                                            onSuccess={getUser}
                                        />
                                    )}

                                    {user && !user.system_defined && hasPermission && hasPermission('delete-user') && (
                                        <div className='social-network theme-form'>
                                            <span className='f-w-600 text-danger'>Danger Zone</span>
                                            <ButtonIcon confirm={{confirm: true}} onclick={() => deleteUser()}
                                                        type={'delete'}/>
                                        </div>
                                    )}

                                </CardBody>
                            </Card>
                        </Col>
                        <Col sm='12' lg='9'>
                            <Card className='hovercard'>
                                <CardBody>
                                    <BootstrapTabContainer>
                                        <BootstrapTabContent title={'Dashboard'} icon={"icofont icofont-dashboard"}>
                                            This is Dashboard
                                        </BootstrapTabContent>

                                        {canAssignUserRole && (
                                            <BootstrapTabContent title={'Role'} icon={'icofont icofont-ui-user'}>
                                                {user && <RoleListView userID={user.id}/>}
                                            </BootstrapTabContent>
                                        )}

                                        {canAssignUserPermission && (
                                            <BootstrapTabContent title={'Permission'}
                                                                 icon={'icofont icofont-shield-alt'}>
                                                {user && <PermissionListView userID={user.id}/>}
                                            </BootstrapTabContent>
                                        )}

                                        {user?.point_wallet_id && canReadWalletTransactions && (
                                            <BootstrapTabContent title={'Points'}
                                                                 icon={'icofont icofont-sale-discount'}>
                                                <WalletTransactionsTable params={{wallet_id: user.point_wallet_id}}/>
                                            </BootstrapTabContent>
                                        )}

                                        {user?.cash_wallet_id && canReadWalletTransactions && (
                                            <BootstrapTabContent title={'Cash'} icon={'icofont icofont-cur-dollar'}>
                                                <WalletTransactionsTable params={{wallet_id: user.cash_wallet_id}}/>
                                            </BootstrapTabContent>
                                        )}

                                        {canReadWalletTransactions && (
                                            <BootstrapTabContent title={'Confirmations'}
                                                                 icon={'icofont icofont-check-alt'}>
                                                <WalletTransactionsTable
                                                    params={{user_id: user?.id, status: 'unconfirmed'}}
                                                    tableOptions={{confirm: true}}/>
                                            </BootstrapTabContent>
                                        )}

                                        {canReadCollectors && (
                                            <BootstrapTabContent title={'Collectors'}
                                                                 icon={'icofont icofont-users-alt-4'}>
                                                <CollectorTable
                                                    params={{'created-user-id': user?.id}}
                                                    tableOptions={{delete: false, view: true}}
                                                />
                                            </BootstrapTabContent>
                                        )}

                                        {hasPermission && hasPermission('read-contact') && (
                                            <BootstrapTabContent title={'Contacts'}
                                                                 icon={'icofont icofont-contact-add'}>
                                                <div className='d-flex justify-content-end mb-3'>
                                                    {user?.id && <ButtonIcon
                                                        attrBtn={{onClick: () => setContactModalVisibility(true)}}
                                                        type={'add'}
                                                        permission={'create-contact'}
                                                    />}
                                                </div>
                                                <ContactTable
                                                    ref={contactTableRef}
                                                    params={{related_key: 'user', related_id: user?.id}}
                                                    related_key={'user'}
                                                    related_id={user?.id}
                                                    onEdit={openContactEditModal}
                                                />
                                            </BootstrapTabContent>
                                        )}

                                        {hasPermission && hasPermission('update-user') && (
                                            <BootstrapTabContent title={'Update'} icon={'icofont icofont-ui-edit'}>
                                                <UpdateUserForm userData={user || undefined}/>
                                            </BootstrapTabContent>
                                        )}

                                        {hasPermission && hasPermission('update-user-password') && (
                                            <BootstrapTabContent title={'Password'} icon={'icofont icofont-key'}>
                                                {user && <UpdatePasswordForm userID={user.id}/>}
                                            </BootstrapTabContent>
                                        )}

                                        {hasPermission && hasPermission('read-activity-log') && (
                                            <BootstrapTabContent title={'Activity Log'} icon={'icofont icofont-list'}>
                                                {user && <ActivityLog subjectType="user" subjectId={user.id}/>}
                                            </BootstrapTabContent>
                                        )}

                                    </BootstrapTabContainer>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </Container>
            {user ? (
                <Modal isOpen={contactModalVisibility} toggle={contactModalToggle} centered>
                    <ModalHeader>{selectedContact ? 'Update' : 'Create'} Contact</ModalHeader>
                    <ModalBody>
                        <ContactForm
                            callBack={addContact}
                            contact={selectedContact}
                            related_key={'user'}
                            related_id={user.id}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Btn attrBtn={{color: 'primary', onClick: contactModalToggle}}>Close</Btn>
                    </ModalFooter>
                </Modal>
            ) : null}
        </div>
    );
};

export default UserShow;
